import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import InGoMap from '../components/maps/InGoMap';
import { useInGoRoad } from '../components/maps/useInGoRoad';
import { useOwnVehicle } from '../components/maps/useOwnVehicle';
import PackagePhotoCapture from '../components/PackagePhotoCapture';
import DriverJobState from '../components/driver/DriverJobState';
import { jobPath, useDriverJob } from '../components/driver/useDriverJob';
import { useLiveLocation } from '../hooks/useLiveLocation';
import DeliveryPin from '../components/DeliveryPin';
import {
  DELIVERY_PIN_DRIVER_HINT,
  DELIVERY_PIN_INCOMPLETE_ERROR,
  isCompleteDeliveryPin,
  normalizeDeliveryCodeInput,
} from '../lib/deliveryConfirmationCode';
import { DRIVER_CANCEL_REASONS } from '../lib/driverBookingCancel';
import { isPackagePhotoSrc } from '../lib/packagePhoto';
import { riderWork } from '../api/riderWork';
import { dataUrlToBlob, deliveredJob, HAND_BACK_REASONS, isOnDropOffLeg, jobFromApi } from '../lib/riderWork';
import './driverDelivery.css';

export default function DriverNavigationPage() {
  const navigate = useNavigate();
  const live = useLiveLocation({ mapThrottleMs: 12000, movePublishMeters: 80 });
  // The rider on their own vehicle, blue: on a delivery.
  const { vehicle: ownKind, position: ownPosition } = useOwnVehicle(live);
  const ownVehicle = useMemo(() => ({ vehicle: ownKind, state: 'delivering' }), [ownKind]);
  const { state } = useLocation();
  const [ending, setEnding] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [deliveryCodeInput, setDeliveryCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReasonId, setCancelReasonId] = useState('breakdown');
  const [cancelNote, setCancelNote] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const { order, status: jobStatus, error: jobError, reload: reloadJob } = useDriverJob();
  // Hooks below must keep running while the job resolves; the render is gated.
  const o = useMemo(() => order || {}, [order]);
  const [driverPhoto, setDriverPhoto] = useState(() =>
    isPackagePhotoSrc(o.driverPackagePhotoDataUrl) ? o.driverPackagePhotoDataUrl : '',
  );
  const [photoErr, setPhotoErr] = useState('');
  const [photoSaving, setPhotoSaving] = useState(false);
  const pickup = String(state?.pickup || o.from || '').trim();
  const dropoff = String(state?.dropoff || o.to || '').trim();
  const [customerName, setCustomerName] = useState(String(o.customerName || '').trim() || 'Customer');
  const [customerPhone, setCustomerPhone] = useState(String(o.customerPhone || '').trim());
  const phase = state?.phase === 'dropoff' ? 'dropoff' : 'pickup';
  /**
   * Main leg: pickup → drop-off (set from active delivery). Legacy: `phase === 'dropoff'`.
   * An InGo job the server says is picked up is on the drop-off leg however the
   * screen was reached (a reload, a restart, a tap from the home card).
   */
  const toDropoff = state?.navLeg === 'toDropoff' || phase === 'dropoff' || isOnDropOffLeg(o);
  const dest = String(state?.dest || (toDropoff ? dropoff : pickup) || '').trim();
  // InGo's job carries its two pins: no geocoding of an address it already placed.
  const pickupGeo = o.pickupPoint || null;
  const dropoffGeo = o.dropoffPoint || null;
  const destGeo = dest ? (toDropoff ? dropoffGeo : pickupGeo) : null;

  useEffect(() => {
    // On InGo's drop-off leg the person to call is the recipient, not the sender.
    const recipient = toDropoff && (o.dropoffContactName || o.dropoffContactPhone);
    setCustomerName(String((recipient ? o.dropoffContactName : o.customerName) || '').trim() || 'Customer');
    setCustomerPhone(String((recipient ? o.dropoffContactPhone : o.customerPhone) || '').trim());
  }, [o, toDropoff]);

  const finishJourney = useCallback(
    async (code) => {
      setEnding(true);
      setCodeError('');
      // InGo checks the client's code; a wrong one says how many tries are left,
      // and too many lock the drop-off for the office (CODE_LOCKED).
      try {
        await riderWork.dropOff(o.deliveryId, code);
      } catch (e) {
        setCodeError(e?.message || 'Could not complete the delivery.');
        setEnding(false);
        return;
      }
      setShowCodeModal(false);
      setDeliveryCodeInput('');
      setEnding(false);
      // Cash on delivery: the rider collects it. Paid online already (ADR 0047):
      // nothing to collect, and the collect step goes straight on to the rating.
      navigate(jobPath('collect-payment', o), { replace: true, state: { order: deliveredJob(o) } });
    },
    [navigate, o],
  );

  // The drop-off is proved by the client's code: completing always asks for it.
  const onEndJourney = useCallback(() => {
    setCodeError('');
    setDeliveryCodeInput('');
    setShowCodeModal(true);
  }, []);

  const onConfirmDeliveryCode = useCallback(
    (e) => {
      e.preventDefault();
      const entered = normalizeDeliveryCodeInput(deliveryCodeInput);
      if (!isCompleteDeliveryPin(entered)) {
        setCodeError(DELIVERY_PIN_INCOMPLETE_ERROR);
        return;
      }
      finishJourney(entered);
    },
    [deliveryCodeInput, finishJourney],
  );

  const onCustomerPickedUp = useCallback(async () => {
    if (!driverPhoto) {
      setPhotoErr('Take or upload a package photo before continuing.');
      return;
    }
    // The pickup is proved by the package's photo, kept by InGo; the job is
    // then on its drop-off leg on the server, whatever happens to this phone.
    setPhotoSaving(true);
    try {
      const job = await riderWork.pickUp(o.deliveryId, dataUrlToBlob(driverPhoto), 'package.jpg');
      setPhotoSaving(false);
      navigate(jobPath('navigation', o), {
        replace: true,
        state: {
          order: { ...o, ...(job ? jobFromApi(job) : { status: 'picked_up' }), driverPackagePhotoDataUrl: driverPhoto },
          pickup,
          dropoff,
          dest: dropoff,
          navLeg: 'toDropoff',
          phase: 'dropoff',
        },
      });
    } catch (e) {
      setPhotoSaving(false);
      setPhotoErr(e?.message || 'Could not save the pickup. Check your connection and try again.');
    }
  }, [navigate, o, pickup, dropoff, driverPhoto]);

  const apiRoad = useInGoRoad(pickupGeo, dropoffGeo);

  const openInMaps = useCallback(() => {
    // An InGo job hands the phone's own maps app the pin (Google Maps, Waze,
    // OsmAnd: the rider's choice), never an address to search and no key.
    if (!destGeo) return;
    const at = `${destGeo.lat},${destGeo.lng}`;
    window.location.href = `geo:${at}?q=${at}(${encodeURIComponent(toDropoff ? 'Drop-off' : 'Pickup')})`;
  }, [destGeo, toDropoff]);

  // An InGo job can be given back only before pickup; after it, the office is called.
  const canCancelTrip = Boolean(o.mayHandBack) && !toDropoff;

  const onConfirmCancelTrip = useCallback(
    async (e) => {
      e.preventDefault();
      if (cancelBusy) return;
      if (cancelReasonId === 'other' && !String(cancelNote || '').trim()) {
        setCancelError('Please type a short note for “Other”.');
        return;
      }
      setCancelBusy(true);
      setCancelError('');
      try {
        await riderWork.handBack(o.deliveryId, HAND_BACK_REASONS[cancelReasonId] || 'other', String(cancelNote || '').trim() || null);
      } catch (e2) {
        setCancelBusy(false);
        setCancelError(e2?.message || 'Could not give the job back.');
        return;
      }
      setCancelBusy(false);
      setShowCancelModal(false);
      navigate('/driver/home', { replace: true, state: { toast: 'Job given back. The next rider is being asked.' } });
    },
    [cancelBusy, cancelNote, cancelReasonId, navigate, o],
  );

  if (jobStatus !== 'ready') {
    return <DriverJobState status={jobStatus} error={jobError} label="trip" onRetry={reloadJob} />;
  }

  return (
    <div className="nav-page" role="application" aria-label="Navigation">
      <div className="nav-mapV nav-mapV--gmap">
        {pickupGeo && dropoffGeo ? (
          <InGoMap
            pins={[{ role: 'pickup', ...pickupGeo }, { role: 'dropoff', ...dropoffGeo }]}
            route={apiRoad}
            rider={ownPosition}
            riderVehicle={ownVehicle}
            label="Driving directions"
            height="100%"
            bleed
          />
        ) : null}
        <div className="nav-trf" />
      </div>
      <div className="nav-ov2" role="region" aria-label="Route actions">
        <p className="nav-destB">{toDropoff ? 'Drop-off' : 'Pickup'}</p>
        <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.35 }}>{dest}</p>
        {!toDropoff && dropoff && dropoff !== pickup ? <p className="nav-txt">Next: {dropoff}</p> : null}
        <div className="nav-cusBox" aria-label="Customer details">
          <p className="nav-cusName">{customerName}</p>
          <p className="nav-cusPhone">{customerPhone || 'Phone not available yet'}</p>
        </div>
        {!toDropoff ? (
          <div className="nav-pkgPhoto">
            <PackagePhotoCapture
              compact
              required
              value={driverPhoto}
              onChange={(url) => {
                setPhotoErr('');
                setDriverPhoto(url || '');
              }}
              label="Package photo"
              hint="Take or upload a photo of the parcel at pickup."
            />
            {photoErr ? (
              <p role="alert" style={{ margin: '0.35rem 0 0', color: '#b91c1c', fontSize: '0.78rem', fontWeight: 700 }}>
                {photoErr}
              </p>
            ) : null}
          </div>
        ) : null}
        {toDropoff ? (
          <p className="nav-codeHint" role="note">
            At drop-off, ask the customer for their 6-digit delivery PIN before you complete the journey.
          </p>
        ) : null}
        <div className="nav-actions">
          <button type="button" className="nav-mainBtn nav-mainBtn--maps" onClick={openInMaps}>
            Open in maps
          </button>
          {!toDropoff ? (
            <button
              type="button"
              className="nav-mainBtn nav-mainBtn--pickup"
              onClick={onCustomerPickedUp}
              disabled={photoSaving}
              aria-label="Parcel picked up, continue to drop-off"
            >
              {photoSaving ? 'Saving photo…' : 'Parcel picked up'}
            </button>
          ) : null}
        </div>
        {toDropoff ? (
          <div className="nav-doneWrap">
            <button
              type="button"
              className="nav-mainBtn nav-mainBtn--done"
              disabled={ending || cancelBusy}
              onClick={onEndJourney}
            >
              <span className="nav-mainBtn__icon" aria-hidden>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
                  <path
                    d="M7.5 12.2l2.6 2.6 6.4-6.8"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>{ending ? 'Confirming…' : 'Complete Delivery'}</span>
            </button>
          </div>
        ) : null}
        {canCancelTrip ? (
          <button
            type="button"
            className="nav-cancelTrip"
            disabled={ending || cancelBusy}
            onClick={() => {
              setCancelError('');
              setShowCancelModal(true);
            }}
          >
            Cancel trip
          </button>
        ) : null}
      </div>
      {showCancelModal ? (
        <div className="nav-codeModal" role="dialog" aria-modal="true" aria-labelledby="nav-cancelModal-title">
          <form className="nav-codeModal__panel" onSubmit={onConfirmCancelTrip}>
            <h2 id="nav-cancelModal-title" className="nav-codeModal__title">
              Cancel trip
            </h2>
            <p className="nav-codeModal__hint">
              Choose a reason. This is saved and shown to the customer and admin.
            </p>
            <label className="nav-codeModal__label" htmlFor="nav-cancel-reason">
              Reason
            </label>
            <select
              id="nav-cancel-reason"
              className="nav-cancelSelect"
              value={cancelReasonId}
              onChange={(e) => {
                setCancelReasonId(e.target.value);
                setCancelError('');
              }}
              disabled={cancelBusy}
            >
              {DRIVER_CANCEL_REASONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
            <label className="nav-codeModal__label" htmlFor="nav-cancel-note">
              Extra details {cancelReasonId === 'other' ? '(required)' : '(optional)'}
            </label>
            <textarea
              id="nav-cancel-note"
              className="nav-cancelNote"
              rows={3}
              maxLength={200}
              placeholder="e.g. flat tyre near Main St"
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              disabled={cancelBusy}
            />
            {cancelError ? (
              <p className="nav-codeModal__error" role="alert">
                {cancelError}
              </p>
            ) : null}
            <div className="nav-codeModal__actions">
              <button
                type="button"
                className="nav-codeModal__btn nav-codeModal__btn--ghost"
                disabled={cancelBusy}
                onClick={() => {
                  if (cancelBusy) return;
                  setShowCancelModal(false);
                  setCancelError('');
                }}
              >
                Keep trip
              </button>
              <button type="submit" className="nav-codeModal__btn nav-codeModal__btn--danger" disabled={cancelBusy}>
                {cancelBusy ? 'Cancelling…' : 'Confirm cancel'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {showCodeModal ? (
        <div className="nav-codeModal" role="dialog" aria-modal="true" aria-labelledby="nav-codeModal-title">
          <form className="nav-codeModal__panel" onSubmit={onConfirmDeliveryCode}>
            <h2 id="nav-codeModal-title" className="nav-codeModal__title">
              Delivery PIN
            </h2>
            <p className="nav-codeModal__hint">{DELIVERY_PIN_DRIVER_HINT}</p>
            <p className="nav-codeModal__label" id="nav-delivery-pin-label">
              Customer PIN
            </p>
            <DeliveryPin
              idPrefix="nav-delivery-pin"
              value={deliveryCodeInput}
              onChange={(next) => {
                setDeliveryCodeInput(next);
                setCodeError('');
              }}
              disabled={ending}
              autoFocus
              ariaLabel="Customer delivery PIN"
            />
            {codeError ? (
              <p className="nav-codeModal__error" role="alert">
                {codeError}
              </p>
            ) : null}
            <div className="nav-codeModal__actions">
              <button
                type="button"
                className="nav-codeModal__btn nav-codeModal__btn--ghost"
                disabled={ending}
                onClick={() => {
                  if (ending) return;
                  setShowCodeModal(false);
                  setDeliveryCodeInput('');
                  setCodeError('');
                }}
              >
                Cancel
              </button>
              <button type="submit" className="nav-codeModal__btn nav-codeModal__btn--primary" disabled={ending}>
                {ending ? 'Confirming…' : 'Confirm delivery'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
